import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Verify from '../../assets/images/Verify';
import { UnVerified } from '../../AssetComponents/Images';
import { getRoleSpecificVerification } from '../../utils/verificationUtils';
import { useNavigate } from 'react-router-dom';

/**
 * VerificationStatus Component
 * 
 * Displays user verification status and handles verification requirements
 * Can be used across the application to show verification status
 */
export const VerificationStatus = ({ 
  showWarning = false, 
  onVerificationRequired = null,
  className = "",
  role = null, // 'host' or 'traveler' for role-specific verification
  profile = null // ✅ NEW: Optional profile prop for viewing other users' profiles
}) => {
  const currentProfile = useSelector((state) => state.auth.profile);
  const account = useSelector((state) => state.auth.account);
  const navigate = useNavigate();

  // ✅ CRITICAL FIX: Use provided profile if available (for viewing other users), otherwise use current profile
  const profileToUse = profile || currentProfile;

  // Use role-specific verification if role is provided
  const verification = role ? 
    getRoleSpecificVerification(profileToUse, role) : 
    {
      isVerified: profileToUse?.isVerified || false,
      verificationLevel: profileToUse?.verificationLevel || "none",
      verificationText: profileToUse?.isVerified ? "Verified Account" : "Unverified Account"
    };

  const isVerified = verification.isVerified;
  const verificationLevel = verification.verificationLevel;

  const handleVerificationClick = () => {
    if (!isVerified) {
      if (onVerificationRequired) {
        onVerificationRequired();
      } else {
        // Fallback: navigate to appropriate profile mode for verification
        const targetMode = role === 'host' ? 'host' : 'traveler';
        navigate(`/dashboard/profile?mode=${targetMode}`);
      }
    }
  };

  const getVerificationText = () => {
    // Use role-specific text if available
    if (role && verification.verificationText) {
      return verification.verificationText;
    }

    if (!isVerified) {
      return "Unverified Account";
    }

    switch (verificationLevel) {
      case "both":
        return "Verified Account (Emirates ID + KYC)";
      case "emirates_id":
        return "Verified Account (Emirates ID)";
      case "kyc":
        return "Verified Account (KYC)";
      default:
        return "Verified Account";
    }
  };

  const getVerificationIcon = () => {
    return isVerified ? <Verify /> : <UnVerified />;
  };

  return (
    <div 
      className={`flex items-center gap-2 ${className} ${!isVerified && showWarning ? 'cursor-pointer' : ''}`}
      onClick={handleVerificationClick}
    >
      {getVerificationIcon()}
      <div className="text-sm sm:text-base">
        {getVerificationText()}
        {!isVerified && showWarning && (
          <div className="text-xs text-red-500 mt-1">
            Verification required for bookings
            <div className="text-xs text-gray-600 mt-1">
              Names should match with verification providers
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * VerificationGuard Component
 * 
 * Prevents unverified users from accessing certain features
 * Shows verification prompt instead of the protected content
 */
export const VerificationGuard = ({ 
  children, 
  fallback = null,
  showPrompt = true 
}) => {
  const currentProfile = useSelector((state) => state.auth.profile);
  const isVerified = currentProfile?.isVerified || false;

  if (isVerified) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
      <UnVerified className="w-16 h-16 text-gray-400 mb-4" />
      <h3 className="text-lg font-semibold text-gray-700 mb-2">
        Verification Required
      </h3>
      <p className="text-sm text-gray-500 text-center mb-4">
        You need to verify your identity to access this feature.
      </p>
      <button
        onClick={() => {
          // Navigate to traveler profile page for verification by default
          window.location.href = '/dashboard/profile?mode=traveler';
        }}
        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
      >
        Verify Now
      </button>
    </div>
  );
};

/**
 * Hook to check verification status
 */
export const useVerificationStatus = () => {
  const currentProfile = useSelector((state) => state.auth.profile);
  
  return {
    isVerified: currentProfile?.isVerified || false,
    verificationLevel: currentProfile?.verificationLevel || "none",
    hasEmiratesId: currentProfile?.verifications?.emiratesId?.verified || false,
    hasKyc: currentProfile?.verifications?.kyc?.verified || false,
    profile: currentProfile
  };
};

/**
 * Utility function to check if user can perform booking
 */
export const canUserBook = (profile) => {
  if (!profile) return false;
  return profile.isVerified || false;
};

/**
 * Utility function to show verification error
 */
export const showVerificationError = (message = "Verification required to make bookings") => {
  toast.error(message, {
    autoClose: 5000,
  });
};

export default VerificationStatus;
